import axios from 'axios';
import { getToken } from './auth.service';
import { Message, Project, User, Organization } from '../types';

// Interface for document structure returned by /api/v1/documents/
export interface ProjectDocument {
  id: string;
  filename: string;
  content_type: string;
  size?: number;
  upload_date?: string;
  status?: string;
  source_type?: string;
  indexes: string[];
  [key: string]: any; // Allow other properties
}

export interface VectorIndex {
  name: string;
  document_count?: number;
  created_at?: string;
  [key: string]: any; // Allow other properties
}

export interface JobDetail {
  job_id: string;
  id: string;
  document_id?: string;
  index_name: string;
  job_type: string;
  status: string; // e.g., 'completed', 'pending', 'processing', 'queued', 'failed'
  created_at: string;
  updated_at: string;
  completed_at?: string;
  progress?: {
    total_units?: number;
    processed_units?: number;
    successful_units?: number;
    failed_units?: number;
    percent_complete?: number;
  };
  error_details?: any;
  error?: string | null;
  job_metadata?: Record<string, any>;
}

export interface IndexDetail {
  documents: ProjectDocument[];
  jobs: JobDetail[];
}

export interface RetrievedSource {
  id?: string | number;
  content: string;
  score: number;
  metadata: {
    title?: string;
    url?: string;
    filename?: string;
    [key: string]: any;
  };
}

export interface ApiErrorData {
  message: string;
  status?: number;
  code?: string;
  data?: any;
}

const createApiError = (error: unknown): ApiErrorData => {
  if (axios.isAxiosError(error)) {
    return {
      message: error.response?.data?.message || error.response?.statusText || error.message || 'Failed to complete request',
      status: error.response?.status,
      code: error.response?.data?.code,
      data: error.response?.data
    };
  }
  return {
    message: error instanceof Error ? error.message : 'An unknown error occurred'
  };
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate axios instance for public endpoints that don't need auth
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error) {
    return Promise.reject(error);
  }
});

// Add this function to ensure projects have the correct fields
const mapProjectFields = (project: any): Project => {
  // Map chat_type to type if type is not present
  if (project.chat_type && !project.type) {
    project.type = project.chat_type;
  }
  
  // Map rag_vector_db to indexName if indexName is not present and it's a RAG project
  if (project.chat_type === 'rag' && project.rag_vector_db && !project.indexName) {
    project.indexName = project.rag_vector_db;
  }
  
  return project;
};

export const apiService = {
  projects: {
    getAll: async (): Promise<Project[]> => {
      try {
        const response = await api.get('/projects');
        // Map each project to ensure it has both chat_type/type and rag_vector_db/indexName fields
        return response.data.map(mapProjectFields);
      } catch (error) {
        throw createApiError(error);
      }
    },

    get: async (id: number): Promise<Project> => {
      try {
        const response = await api.get(`/projects/${id}`);
        // Map the project to ensure it has both chat_type/type and rag_vector_db/indexName fields
        return mapProjectFields(response.data);
      } catch (error) {
        throw createApiError(error);
      }
    },

    create: async (project: Omit<Project, 'id'>): Promise<Project> => {
      try {
        const response = await api.post('/projects', project);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    update: async (id: number, project: Partial<Project>): Promise<Project> => {
      try {
        const response = await api.put(`/projects/${id}`, project);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    delete: async (id: number): Promise<void> => {
      try {
        await api.delete(`/projects/${id}`);
      } catch (error) {
        throw createApiError(error);
      }
    },
  },

  messages: {
    create: async (projectId: number, message: Omit<Message, 'id'>): Promise<Message> => {
      try {
        const response = await api.post(`/projects/${projectId}/messages`, message);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },
    
    update: async (projectId: number, messageId: number, messageData: Partial<Message>): Promise<Message> => {
      try {
        const response = await api.put(`/projects/${projectId}/messages/${messageId}`, messageData);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    batchCreate: async (projectId: number, messages: Omit<Message, 'id'>[]): Promise<Message[]> => {
      try {
        const response = await api.post(`/projects/${projectId}/messages/batch`, { messages });
        return response.data;
      } catch (error) {
        console.error('Error caught in batchCreate:', {
          errorType: axios.isAxiosError(error) ? 'AxiosError' : typeof error,
          status: axios.isAxiosError(error) ? error.response?.status : undefined,
          statusText: axios.isAxiosError(error) ? error.response?.statusText : undefined,
          data: axios.isAxiosError(error) ? error.response?.data : undefined,
          message: error instanceof Error ? error.message : undefined
        });

        const apiError = createApiError(error);
        console.error('Created API Error:', { ...apiError });
        throw apiError;
      }
    },

    getByProject: async (projectId: number): Promise<Message[]> => {
      try {
        const response = await api.get(`/projects/${projectId}/messages`);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },
    
    get: async (projectId: number, messageId: number): Promise<Message> => {
      try {
        const response = await api.get(`/projects/${projectId}/messages/${messageId}`);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    delete: async (projectId: number, messageIds: number[]): Promise<void> => {
      try {
        await api.delete(`/projects/${projectId}/messages`, { data: { messageIds } });
      } catch (error) {
        throw createApiError(error);
      }
    },
  },

  users: {
    syncAuth0User: async (auth0User: any): Promise<User> => {
      try {
        const response = await api.post('/users/auth0-sync', auth0User);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    getCurrentUser: async (): Promise<User> => {
      try {
        const response = await api.get('/users/me');
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    getAll: async (): Promise<User[]> => {
      try {
        const response = await api.get('/users');
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    delete: async (sub: string): Promise<void> => {
      try {
        await api.delete(`/users/${sub}`);
      } catch (error) {
        throw createApiError(error);
      }
    },
  },

  organizations: {
    getByToken: async (token: string): Promise<Organization> => {
      // This is a public endpoint, so we use the publicApi instance
      try {
        const response = await publicApi.get(`/organizations/token/${encodeURIComponent(token)}`);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    getById: async (organizationId: string): Promise<Organization> => {
      // This is a public endpoint, so we use the publicApi instance
      try {
        const response = await publicApi.get(`/organizations/id/${encodeURIComponent(organizationId)}`);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    getAll: async (): Promise<Organization[]> => {
      try {
        const response = await api.get('/organizations');
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },

    create: async (organization: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>): Promise<Organization> => {
      try {
        const response = await api.post('/organizations', organization);
        return response.data;
      } catch (error) {
        throw createApiError(error);
      }
    },
  },

  rag: {
    // Get all documents for the current project
    listProjectDocuments: async (): Promise<ProjectDocument[]> => {
      try {
        console.log('Fetching all project documents');
        const response = await api.get('/rag-proxy/documents');
        console.log('Project documents response:', response.data);
        
        if (Array.isArray(response.data)) {
          return response.data as ProjectDocument[];
        } else if (response.data.documents && Array.isArray(response.data.documents)) {
          return response.data.documents as ProjectDocument[];
        }
        
        // If we can't determine the format, return empty array
        console.warn('Unknown response format from documents API:', response.data);
        return [];
      } catch (error) {
        console.error('Error fetching project documents:', error);
        if (axios.isAxiosError(error)) {
          console.error('Axios error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
          });
        }
        throw createApiError(error);
      }
    },

    // List all vector indexes
    listIndexes: async (): Promise<VectorIndex[]> => {
      try {
        console.log('Fetching vector indexes');
        const response = await api.get('/rag-proxy/list_indexes');
        console.log('Vector indexes response:', response.data);
        
        // Handle new response format which is a direct array of index objects
        if (Array.isArray(response.data)) {
          return response.data.map((item: any) => 
            typeof item === 'string' ? { name: item } : item
          );
        } else if (response.data.indexes && Array.isArray(response.data.indexes)) {
          return response.data.indexes.map((item: any) => 
            typeof item === 'string' ? { name: item } : item
          );
        } else if (typeof response.data === 'object') {
          // Try to extract array from an object
          const possibleArrays = Object.values(response.data).filter(val => Array.isArray(val));
          if (possibleArrays.length > 0) {
            return (possibleArrays[0] as any[]).map((item: any) => 
              typeof item === 'string' ? { name: item } : item
            );
          }
        }
        
        // If we can't determine the format, return empty array
        console.warn('Unknown response format from list_indexes:', response.data);
        return [];
      } catch (error) {
        console.error('Error fetching vector indexes:', error);
        if (axios.isAxiosError(error)) {
          console.error('Axios error details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
          });
        }
        throw createApiError(error);
      }
    },
    
    // Create a new vector index
    createIndex: async (name: string): Promise<VectorIndex> => {
      try {
        console.log('Creating new index:', name);
        const response = await api.post('/rag-proxy/indexes', { name });
        console.log('Create index response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error creating index:', error);
        throw createApiError(error);
      }
    },
    
    // Get detailed information about a vector index
    getIndexDetail: async (indexId: string): Promise<IndexDetail> => {
      try {
        const response = await api.get(`/rag-proxy/index/${encodeURIComponent(indexId)}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching index detail:', error);
        throw createApiError(error);
      }
    },
    
    // Query for relevant documents
    retrieveDocuments: async (
      query_text: string,
      indexName: string,
      topK: number = 3,
      threshold: number = 0.0
    ): Promise<RetrievedSource[]> => {
      try {
        const response = await api.get('/rag-proxy/retrieve', {
          params: {
            query_text,
            index_name: indexName,
            top_k: topK,
            threshold
          }
        });
        
        // Handle the new response format which has results[].document
        if (response.data.documents) {
          return response.data.documents;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          return response.data.results.map((result: any) => ({
            id: result.document.id,
            content: result.document.content,
            score: result.score,
            metadata: {
              title: result.document.title,
              url: result.document.url,
              filename: result.document.filename,
            },
          }));
        }
        
        return [];
      } catch (error) {
        console.error('Error retrieving documents:', error);
        throw createApiError(error);
      }
    },

    // Delete a document from an index
    deleteDocumentFromIndex: async (documentId: string, indexName: string): Promise<void> => {
      try {
        console.log(`Deleting document ${documentId} from index ${indexName}`);
        const response = await api.delete(`/rag-proxy/documents/${encodeURIComponent(documentId)}/indexes/${encodeURIComponent(indexName)}`);
        console.log('Delete document response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error deleting document from index:', error);
        throw createApiError(error);
      }
    },

    addDocumentToIndex: async (documentId: string, indexName: string): Promise<{job_id: string}> => {
      try {
        const response = await api.post(`/rag-proxy/documents/${encodeURIComponent(documentId)}/add-to-index`, {
          indexName
        });
        return response.data;
      } catch (error) {
        console.error('Error adding document to index:', error);
        throw createApiError(error);
      }
    },

    addContentToIndex: async (documentId: string, indexName: string): Promise<{job_id: string}> => {
      try {
        const response = await api.post(`/rag-proxy/documents/${encodeURIComponent(documentId)}/add-content-to-index`, {
          indexName
        });
        return response.data;
      } catch (error) {
        console.error('Error adding content to index:', error);
        throw createApiError(error);
      }
    },

    downloadDocument: async (documentId: string): Promise<{ blob: Blob; contentType?: string; filename?: string }> => {
      try {
        const response = await api.get(`/rag-proxy/documents/${encodeURIComponent(documentId)}/download`, {
          responseType: 'blob'
        });
        const contentType = response.headers['content-type'];
        const contentDisposition = response.headers['content-disposition'] as string | undefined;
        let filename: string | undefined;
        if (contentDisposition) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
          filename = decodeURIComponent((match?.[1] || match?.[2] || '').trim());
        }
        return { blob: response.data as Blob, contentType, filename };
      } catch (error) {
        console.error('Error downloading document:', error);
        throw createApiError(error);
      }
    },

    getDocument: async (documentId: string): Promise<any> => {
      try {
        const response = await api.get(`/rag-proxy/documents/${encodeURIComponent(documentId)}`);
        return response.data;
      } catch (error) {
        console.error('Error getting document detail:', error);
        throw createApiError(error);
      }
    },

    deleteDocument: async (documentId: string): Promise<any> => {
      try {
        const response = await api.delete(`/rag-proxy/documents/${encodeURIComponent(documentId)}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting document:', error);
        throw createApiError(error);
      }
    },

    // Upload crawl content (JSON files)
    uploadCrawlContent: async (file: File, title: string): Promise<any> => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title);

        const response = await api.post('/rag-proxy/contents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('Error uploading crawl content:', error);
        throw createApiError(error);
      }
    },

    // Upload document files (txt, pdf, etc.)
    uploadDocument: async (file: File, title?: string): Promise<any> => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (title) {
          formData.append('title', title);
        }

        const response = await api.post('/rag-proxy/documents/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('Error uploading document:', error);
        throw createApiError(error);
      }
    },
  },
};

export { api };
